import { zodResolver } from '@hookform/resolvers/zod';
import { type ResetPasswordDto, ResetPasswordSchema } from '@nestjs-boilerplate/types';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useResetPassword } from '../hooks/use-reset-password';

interface ResetPasswordFormProps {
  token: string;
}

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { mutate: submitResetPassword, isPending } = useResetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<ResetPasswordDto>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      token,
      password: '',
      confirmPassword: '',
    },
  });

  const password = form.watch('password');

  // Password validation checks
  const hasMinLength = password.length >= 8;
  const hasLettersAndNumbers = /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);

  function onSubmit(data: ResetPasswordDto) {
    submitResetPassword(data);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#F5F7F9]">
      <Link
        to="/login"
        className="fixed left-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-[#D4DAE0] text-[#131313]"
      >
        <ChevronLeft className="h-5 w-5" />
      </Link>

      <Card className="w-[390px] min-h-[418px] rounded-[5px] border border-[#D4DAE0] bg-white p-6">
        <CardHeader className="space-y-6 p-0 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

          <div className="space-y-3">
            <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
              Reset Password
            </h1>
            <p className="text-left text-[18px] font-normal leading-[140%] text-[#051923]">
              Please enter your new password below.
            </p>
          </div>
        </CardHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent className="mt-6 space-y-6 p-0">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="************"
                          autoComplete="new-password"
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="************"
                          autoComplete="new-password"
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password requirements */}
              <div className="space-y-2">
                <p className="text-[14px] font-medium text-[#051923]">Password must:</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className={`h-4 w-4 ${hasMinLength ? 'text-green-600' : 'text-gray-300'}`}
                    />
                    <span
                      className={`text-[14px] ${hasMinLength ? 'text-[#051923]' : 'text-gray-400'}`}
                    >
                      Be at least 8 characters long
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className={`h-4 w-4 ${hasLettersAndNumbers ? 'text-green-600' : 'text-gray-300'}`}
                    />
                    <span
                      className={`text-[14px] ${hasLettersAndNumbers ? 'text-[#051923]' : 'text-gray-400'}`}
                    >
                      Be a mixture of letters and numbers
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      className={`h-4 w-4 ${hasUppercase ? 'text-green-600' : 'text-gray-300'}`}
                    />
                    <span
                      className={`text-[14px] ${hasUppercase ? 'text-[#051923]' : 'text-gray-400'}`}
                    >
                      Contain at least 1 upper case letter
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
                disabled={isPending}
              >
                {isPending ? 'Saving...' : 'Save Password'}
              </Button>
            </CardContent>
          </form>
        </Form>
      </Card>
    </div>
  );
}
